import { RouteName } from './RouteName';

/**
 * Enum defining permissions from user tools and team addon.
 */
export enum UserPermission {
  /**
   * API-tier access (docs, PlbPy, shared Resources). Route-level `allow` only —
   * still requires (UserPermission.UNBLOCK) via the global gate.
   */
  API = 'user:api',
  /**
   * Active account. Global gate in `getAreAllRouteLevelsAuthorized`, not a product tier
   * (UserPermission.PLATFORM / UserPermission.API).
   */
  UNBLOCK = 'user:unblock',
  /**
   * Main platform UI tier. Route-level `allow` only (usually on parents) — not a
   * global gate, since not every active (UserPermission.UNBLOCK) user needs
   * platform access and not every authorized route requires it. Complements
   * `UNBLOCK` (account status).
   */
  PLATFORM = 'user:platform',
  RESEARCH = 'user:research',
  ADMIN = 'user:admin',
  READ_ONLY_PLATFORM = 'user:read_only_platform',

  ADMIN_GENERATE_BEARER_TOKEN = 'user:admin.generate_bearer_token',
  ADMIN_SEARCH_STRATEGIES_EXACT_MATCH = 'user:admin.search_strategies_exact_match',
  IMPERSONATE = 'user:impersonate',
  WARN_IMPERSONATE = 'user:impersonate.warn',

  STRATEGY = 'user:strategy',
  STRATEGY_PDF = 'user:strategy.pdf',
  STRATEGY_EXCEL = 'user:strategy.excel',

  PORTFOLIO = 'user:portfolio',
  PORTFOLIO_PDF = 'user:portfolio.pdf',
  PORTFOLIO_EXCEL = 'user:portfolio.excel',

  PCA = 'user:pca',
  PCA_PDF = 'user:pca.pdf',
  PCA_EXCEL = 'user:pca.excel',

  REGRESSION = 'user:regression',
  REGRESSION_PDF = 'user:regression.pdf',
  REGRESSION_EXCEL = 'user:regression.excel',

  CONSTITUENT = 'user:constituent',
  CONSTITUENT_PDF = 'user:constituent.pdf',
  CONSTITUENT_EXCEL = 'user:constituent.excel',

  DEFAULT = 'user:default',
  DISCOVER = 'user:discover',
  MORNING_STAR = 'user:morningstar',
  READ_ONLY = 'user:readonly',
  IMPERSONATE_TEAM = 'user:impersonate_team',

  /**
   * Swap Details access rights from team addon permission
   */
  SWAP_DETAILS = 'user:swap_details',
  SWAP_DETAILS_EXCEL = 'user:swap_details:excel',

  /**
   * Drop Zone access rights from team addon permission
   */
  DROP_ZONE = 'user:dropzone',
  DROP_ZONE_PORTFOLIO_SNAPSHOT = 'user:dropzone:portfolio_snapshot',
  DROP_ZONE_CUSTOM_WEIGHTING = 'user:dropzone:custom_weighting',
  DROP_ZONE_PRIVATE_TRACKS = 'user:dropzone:private_tracks',
  DROP_ZONE_LIVE_SNAPSHOT = 'user:dropzone:live_snapshot',

  /**
   * Strategy Performance access rights from team addon permission
   */
  STRATEGY_PERFORMANCE = 'user:strategy:performance',
  STRATEGY_PERFORMANCE_CONTRIBUTION = 'user:strategy:performance:contribution',
  STRATEGY_PERFORMANCE_RECONCILIATION = 'user:strategy:performance:reconciliation',
  STRATEGY_PERFORMANCE_PDF = 'user:strategy:performance:pdf',

  /**
   * Position Page access rights from tool plan permission
   */
  POSITION = 'user:position',
  POSITION_PDF = 'user:position:pdf',
  POSITION_EXCEL = 'user:position:excel',

  /**
   * Code Whisperer access rights from team addon permission
   */
  CODE_WHISPERER = 'user:code_whisperer',

  /**
   * Risk History access rights from team addon permission
   */
  RISK_HISTORY = 'user:risk_history',

  /**
   * Barra Factor access rights from team addon permission
   */
  BARRA_FACTOR = 'user:barra_factor',

  /**
   * Equity Screener access rights from team addon permission
   */
  EQUITY_SCREENER = 'user:equity_screener',

  /**
   * Basket access rights from team addon permission
   */
  EQUITY_BASKET_CREATION = 'user:basket:equity_basket:creation',
  EQUITY_BASKET_SUBMISSION = 'user:basket:equity_basket:submission',
  QIS_BASKET_CREATION = 'user:basket:qis_basket:creation',
  QIS_BASKET_SUBMISSION = 'user:basket:qis_basket:submission',
  BASKET_OPTIMIZER_SUBMISSION = 'user:basket:optimizer:submission',

  // Request QIS access rights from team addon permission
  REQUEST_QIS = 'user:request_qis',

  // User roles
  SUPER_ADMIN = 'user:admin.super_admin',
  CMS_ADMIN = 'user:admin.cms_admin',
  PLATFORM_ANALYTICS_ADMIN = 'user:admin.platform_analytics_admin',
  PDF_MANAGEMENT_ADMIN = 'user:admin.pdf_management_admin',
  TRACK_MANAGEMENT_ADMIN = 'user:admin.track_management_admin',
  CONSTITUENT_MANAGEMENT_ADMIN = 'user:admin.constituent_management_admin',
  PERFORMANCE_CONTRIBUTION_ADMIN = 'user:admin.performance_contribution_admin',
  ACCOUNT_MANAGEMENT_ADMIN = 'user:admin.account_management_admin',
  FUNCTIONAL_RIGHTS_ADMIN = 'user:admin.functional_rights_admin',
  TRACK_ACCESS_RIGHT_ADMIN = 'user:admin.track_access_right_admin',
  CONSTITUENT_RIGHTS_ADMIN = 'user:admin.constituent_rights_admin',
  BASKET_REQUEST_ADMIN = 'user:admin.basket_request_admin',
}

/**
 * Roles that open at least one `/admin-console` section (each has a matching child `allow`).
 * Used for the parent route gate and default landing map.
 */
export const AdminConsoleSectionRoles = {
  SUPER_ADMIN: UserPermission.SUPER_ADMIN,
  CMS_ADMIN: UserPermission.CMS_ADMIN,
  PLATFORM_ANALYTICS_ADMIN: UserPermission.PLATFORM_ANALYTICS_ADMIN,
  PDF_MANAGEMENT_ADMIN: UserPermission.PDF_MANAGEMENT_ADMIN,
  TRACK_MANAGEMENT_ADMIN: UserPermission.TRACK_MANAGEMENT_ADMIN,
  CONSTITUENT_MANAGEMENT_ADMIN: UserPermission.CONSTITUENT_MANAGEMENT_ADMIN,
  PERFORMANCE_CONTRIBUTION_ADMIN: UserPermission.PERFORMANCE_CONTRIBUTION_ADMIN,
  ACCOUNT_MANAGEMENT_ADMIN: UserPermission.ACCOUNT_MANAGEMENT_ADMIN,
  BASKET_REQUEST_ADMIN: UserPermission.BASKET_REQUEST_ADMIN,
} as const;

export type AdminConsoleSectionRole = (typeof AdminConsoleSectionRoles)[keyof typeof AdminConsoleSectionRoles];

/**
 * Assignable admin roles — section roles plus rights-only roles (no console section of their own).
 * Prefer this over the full permission bag for `userRoles` / `teamRoles`.
 */
export const AdminRoles = {
  ...AdminConsoleSectionRoles,
  FUNCTIONAL_RIGHTS_ADMIN: UserPermission.FUNCTIONAL_RIGHTS_ADMIN,
  TRACK_ACCESS_RIGHT_ADMIN: UserPermission.TRACK_ACCESS_RIGHT_ADMIN,
  CONSTITUENT_RIGHTS_ADMIN: UserPermission.CONSTITUENT_RIGHTS_ADMIN,
} as const;

export type AdminRoles = (typeof AdminRoles)[keyof typeof AdminRoles];

const ADMIN_GATED_ROUTE_PERMISSIONS = new Set<string>([UserPermission.ADMIN, ...Object.values(AdminRoles)]);

/**
 * Route meta `allow` values for admin-gated pages (denied access → NOT_FOUND).
 * `AdminRoles` plus legacy `UserPermission.ADMIN` — not in `AdminRoles`, still used by
 * Sandbox and My Lab proxy table until those routes are migrated.
 */
export function isAdminGatedRoutePermission(permission: UserPermission): boolean {
  return ADMIN_GATED_ROUTE_PERMISSIONS.has(permission);
}

export const defaultAdminRouteMap: Record<AdminConsoleSectionRole, RouteName> = {
  [AdminRoles.SUPER_ADMIN]: RouteName.ADMIN_CONSOLE_TEAMS_MANAGEMENT,
  [AdminRoles.CMS_ADMIN]: RouteName.ADMIN_CONSOLE_CONTENT_MANAGEMENT,
  [AdminRoles.PLATFORM_ANALYTICS_ADMIN]: RouteName.ADMIN_CONSOLE_PLATFORM_ANALYTICS,
  [AdminRoles.PDF_MANAGEMENT_ADMIN]: RouteName.ADMIN_CONSOLE_PDF_MANAGEMENT,
  [AdminRoles.TRACK_MANAGEMENT_ADMIN]: RouteName.ADMIN_CONSOLE_TRACK_MANAGEMENT_STRATEGIES,
  [AdminRoles.CONSTITUENT_MANAGEMENT_ADMIN]: RouteName.ADMIN_CONSOLE_CONSTITUENT_MANAGEMENT,
  [AdminRoles.PERFORMANCE_CONTRIBUTION_ADMIN]: RouteName.ADMIN_CONSOLE_PERFORMANCE_CONTRIBUTION_CONFIG,
  [AdminRoles.ACCOUNT_MANAGEMENT_ADMIN]: RouteName.ADMIN_CONSOLE_USER_MANAGEMENT,
  [AdminRoles.BASKET_REQUEST_ADMIN]: RouteName.ADMIN_CONSOLE_BASKET_REQUEST,
};

/** Landing route for a section role; `undefined` for missing / rights-only admin roles. */
export function getDefaultAdminRouteName(role: AdminRoles | null | undefined): RouteName | undefined {
  if (role == null || !(role in defaultAdminRouteMap)) {
    return undefined;
  }

  return defaultAdminRouteMap[role as AdminConsoleSectionRole];
}
